import type {
  ChannelType
} from '../../hooks/useOnboardingState'

const RESUMABLE_CHANNELS = [
  'whatsapp',
  'slack',
  'imessage',
] as const satisfies readonly ChannelType[]

export type ResumableChannel = (typeof RESUMABLE_CHANNELS)[number]

const RESUMABLE_CHANNEL_SET: ReadonlySet<string> = new Set(RESUMABLE_CHANNELS)
const STORAGE_KEY_PREFIX = 'catch.onboarding.channelResume'

interface StoredChannelResume {
  channel: ResumableChannel
  expiresAt?: string
}

function storageKey(userId: number): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`
}

export function isResumableChannel(
  channel: ChannelType,
): channel is ResumableChannel {
  return RESUMABLE_CHANNEL_SET.has(channel)
}

export function readChannelResume(
  userId: number | undefined,
): ResumableChannel | null {
  if (userId == null) return null

  try {
    const key = storageKey(userId)
    const raw = sessionStorage.getItem(key)
    if (raw == null) return null

    // Keep users who started a connection before the JSON marker shipped on a
    // valid recovery path across the deploy.
    if (RESUMABLE_CHANNEL_SET.has(raw)) return raw as ResumableChannel

    const stored = JSON.parse(raw) as StoredChannelResume | null
    if (!stored || !RESUMABLE_CHANNEL_SET.has(stored.channel)) return null
    if (stored.expiresAt) {
      const expiresAt = new Date(stored.expiresAt).getTime()
      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
        sessionStorage.removeItem(key)
        return null
      }
    }
    return stored.channel
  } catch {
    return null
  }
}

export function writeChannelResume(
  userId: number | undefined,
  channel: ResumableChannel,
  expiresAt?: string,
): void {
  if (userId == null) return

  try {
    const stored: StoredChannelResume = { channel, ...(expiresAt ? { expiresAt } : {}) }
    sessionStorage.setItem(storageKey(userId), JSON.stringify(stored))
  } catch {
    // Storage can be unavailable in private browsing. The parent component's
    // in-memory state still keeps the recovery prompt for this page visit.
  }
}

export function clearChannelResume(userId: number | undefined): void {
  if (userId == null) return

  try {
    sessionStorage.removeItem(storageKey(userId))
  } catch {
    // Non-fatal: a stale session-only marker can only re-show the resume card.
  }
}
